import { observer } from 'mobx-react-lite'
import { Alert, Box, type IDropdownItem, TextFieldWithLabel, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { getCDNAssetURL } from 'shared/lib'
import einExample from 'shared/assets/images/einExample.png'
import { LinkButton } from 'pages/settings/ui/LinkButton/LinkButton'
import { IRSEINDocumentUploader } from 'pages/settings/pages/compliance/ui/IRSEINDocumentUploader'
import styles from './styles.module.scss'
import type { IGeneralInfoStore } from './CommonGeneralInfo'

type IManuallyBusinessFindProps = {
  generalInfoStore: IGeneralInfoStore
  states: IDropdownItem[]
  fieldsError: Record<string, string> | null
  fieldsErrorCommonText: JSX.Element[] | null
}

export const ManuallyBusinessFind = observer(
  ({
    generalInfoStore,
    states,
    fieldsError,
    fieldsErrorCommonText,
  }: IManuallyBusinessFindProps) => {
    const {
      einItem,
      einSearch,
      zipCode,
      isUsaLocation,
      zipLocalErrorMessage,
      setEinSearch,
      addZipManually,
      checkZipLengthManually,
      addCityManually,
      addBusinessNameManually,
      addBusinessAddressManually,
      addBusinessAddress2Manually,
      addBusinessStateManually,
      irsEINDocumentUploaderStore,
    } = generalInfoStore

    const einText = isUsaLocation
      ? 'Employer Identification Number (EIN)'
      : 'Canadian Business Number (BN)'

    const numberType = isUsaLocation ? 'EIN' : 'BN'

    const cityPlaceholder = isUsaLocation ? 'e.g. Los Angeles' : 'e.g. Toronto'
    const linkToInfo = getCDNAssetURL(einExample)

    const whereCanIFindButton = isUsaLocation && (
      <LinkButton text={'Where can I find it?'} link={linkToInfo} />
    )

    return (
      <Box>
        <Box marginBottom={'26px'}>
          <TextFieldWithLabel
            labelProps={{
              label: {
                text: einText,
                rightNode: whereCanIFindButton,
              },
            }}
            textFieldProps={{
              className: styles.textInput,
              value: einSearch,
              onChange: setEinSearch,
              InputProps: {
                placeholder: 'e.g. 12-3456789',
              },
            }}
          />
        </Box>

        <Box marginBottom={3}>
          <TextFieldWithLabel
            errorBorder={Boolean(fieldsError?.business_name)}
            labelProps={{
              label: {
                text: 'Legal business name',
                rightNode: whereCanIFindButton,
              },
            }}
            textFieldProps={{
              className: styles.textInput,
              value: einItem?.name || '',
              onChange: addBusinessNameManually,
              InputProps: {
                placeholder: 'e.g. Example LLC',
              },
            }}
            bottomLabelProps={{
              label: {
                text: (
                  <Typography variant='text-input-helper' ariaLabel='legal_business_name'>
                    {`Enter the exact legal business name, as registered with the ${numberType}`}
                  </Typography>
                ),
              },
            }}
          />

          {fieldsErrorCommonText?.length && (
            <Alert
              item={{ desc: fieldsErrorCommonText, type: 'error' }}
              marginBottom={32}
              marginTop={'26px'}
            />
          )}
        </Box>

        <TextFieldWithLabel
          marginBottom={3}
          errorBorder={Boolean(fieldsError?.street_address_1)}
          labelProps={{
            label: {
              text: 'Business address',
              rightNode: whereCanIFindButton,
            },
            marginBottom: 4,
          }}
          textFieldProps={{
            className: styles.textInput,
            value: einItem?.street1 || '',
            onChange: addBusinessAddressManually,
            InputProps: {
              placeholder: 'Enter your address',
            },
          }}
          bottomLabelProps={{
            label: {
              text: (
                <Typography variant='text-input-helper' ariaLabel='business_address'>
                  {` Enter the exact business address, as registered with the ${numberType}`}
                </Typography>
              ),
            },
          }}
        />

        <TextFieldWithLabel
          marginBottom={3}
          errorBorder={Boolean(fieldsError?.street_address_2)}
          labelProps={{
            label: {
              text: 'Business address 2 (optional)',
            },
          }}
          textFieldProps={{
            className: styles.textInput,
            value: einItem?.street2 || '',
            onChange: addBusinessAddress2Manually,
            InputProps: {
              placeholder: 'e.g. Building name',
            },
          }}
        />

        <TextFieldWithLabel
          marginBottom={3}
          errorBorder={Boolean(fieldsError?.city)}
          labelProps={{
            label: {
              text: 'City',
            },
          }}
          textFieldProps={{
            className: styles.textInput,
            value: einItem?.city || '',
            onChange: addCityManually,
            InputProps: {
              placeholder: cityPlaceholder,
            },
          }}
        />

        <Box marginBottom={'16px'} display='flex' justifyContent='space-between' gap={'12px'}>
          <Box flex={1}>
            <SelectField
              value={einItem?.state}
              items={states}
              setValue={addBusinessStateManually}
              btnProps={{
                size: 'large',
                style: {
                  height: '40px',
                },
                tabIndex: -1,
              }}
              labelProps={{
                label: {
                  text: isUsaLocation ? 'State' : 'Province',
                },
              }}
              dropdownProps={{
                tabIndex: 0,
              }}
            />
          </Box>
          <Box flex={1}>
            <TextFieldWithLabel
              errorBorder={Boolean(fieldsError?.zip_code)}
              errorMessage={zipLocalErrorMessage || undefined}
              marginBottom={0}
              labelProps={{
                label: {
                  text: isUsaLocation ? 'Zip code' : 'Postal code',
                },
              }}
              textFieldProps={{
                value: zipCode,
                className: styles.numberOfCreditsInput,
                onChange: addZipManually,
                onBlur: checkZipLengthManually,
                InputProps: {
                  placeholder: isUsaLocation ? 'e.g. 90001' : 'e.g. A1A 1A1',
                },
              }}
            />
          </Box>
        </Box>

        {isUsaLocation && <IRSEINDocumentUploader store={irsEINDocumentUploaderStore} />}
      </Box>
    )
  }
)
